/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import axios from "axios";
import queryString from "query-string";
import {
  Alert,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
} from "@mui/material";
import Card from "../../components/Card/Card.jsx";
import CardBody from "../../components/Card/CardBody.jsx";
import CardFooter from "../../components/Card/CardFooter.jsx";
import WithUser from "../../ContextAndHOC/WithUser";
import { Formik, Form, Field } from "formik";
import { string, object, boolean, ref } from "yup";
import FormikTextField from "../../components/FormikValidatedComponents/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import {
  GuestLoginSignUpPasswordCommonCss,
  CommonStyleObject,
} from "../../components/CSS/CommonCss";
import { withTranslation } from "react-i18next";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import { Link, Navigate } from "react-router";
import { Helmet } from "react-helmet";
import { withRouter } from "../../ContextAndHOC/WithRouter";
import FormikCheckBox from "../../components/FormikValidatedComponents/CheckBox";
import { API_MOUNT_PATH, isEdgeBrowser } from "../../constants";
import { withMediaQuery } from "../../components/CustomInput/CustomMaterialUIWithWidth";
import ResetPasswordStyleObject from "../../components/CSS/ResetPassWordStyle.jsx";
import Analytics from "@aws-amplify/analytics";
import { getUserDetailObject, getRoleBaseRedirectPath } from "./utils.js";
import { SignUpStyleObject } from "../../components/CSS/SignUPPageStyle.jsx";
import {
  handleTermCondition,
  handleuserguide,
} from "../../components/CommonFunction";
import PropTypes from "prop-types";

const hostURL = window.location.host.includes("localhost");

function ResetPassword(props) {
  const {
    t,
    currentUser: { userDetails, refreshTokens, changeUser },
    theme,
    location,
    navigate,
    i18n,
  } = props;

  const [state, setState] = useState({
    renderto: "",
    resetPassworSuccess: true,
    userError: "",
    loading: true,
    invalidToken: "",
    termsCondition: false,
    language: i18n.language,
    country: "",
    loginSucess: false,
    isLoginCompleted: false,
    passwordShown: "password",
    passwordShownRecheck: "password",
    forceRedirect: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      const parsed = queryString.parse(location.search);
      const token = { token: parsed.reset_token };
      try {
        const result = await axios.post(
          `${API_MOUNT_PATH}/providerauthservice/checkfirsttimelogin`,
          token
        );
        if (result.data.status === 2) {
          navigate("/guest/ForgetPassword", { state: "error" });
        } else if (result.data.status === 1) {
          setState((prev) => ({
            ...prev,
            termsCondition: true,
            country: result.data.country,
            loading: false,
          }));
        } else {
          setState((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        setState((prev) => ({ ...prev, loading: false }));
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handlePassword = (evt) => {
    if (state.userError) {
      setState((prev) => ({ ...prev, userError: "" }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setState((prev) => ({
      ...prev,
      [field]: prev[field] === "password" ? "text" : "password",
    }));
  };

  const handleSubmit = async (values) => {
    const parsed = queryString.parse(location.search);
    const isFromSignUp = location?.state?.is_from_signup;
    const data = {
      token: parsed.reset_token,
      signup: parsed.signup,
      password: values.password,
      cnfpassword: values.cnfpassword,
      acceptTerms: values.acceptTerms,
    };
    if (state.termsCondition && values.isGdpr) {
      data.dataConsentFlag = values.gdprDataSharing;
    }
    try {
      const response = await axios.post(
        API_MOUNT_PATH +
          `/providerauthservice/${
            isFromSignUp ? "setuppassword" : "resetpassword"
          }`,
        data
      );
      if (response.data.status === 1) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
        if (!response.data.userrole) {
          await refreshTokens();
          setState((prev) => ({
            ...prev,
            renderto: "/firsttimelogin",
            forceRedirect: true,
            loginSucess: true,
            isLoginCompleted: true,
          }));
          changeUser({ country: response.data.country });
        } else {
          setState((prev) => ({
            ...prev,
            renderto: getRoleBaseRedirectPath(response.data.userrole),
            loginSucess: true,
            isLoginCompleted: true,
          }));
          await refreshTokens();
          const userObject = getUserDetailObject(response.data, t);
          changeUser(userObject);
          try {
            await Analytics.updateEndpoint({
              userAttributes: {
                role: [response.data.userrole],
                uID: [response.data.userid],
              },
            });
          } catch (e) {
            console.log("Analytics error", e);
          }
          //proceeding without blocking user if analytics fails
        }
      } else {
        setState((prev) => ({
          ...prev,
          invalidToken: t("common.COMMON__MISSING_RESET_TOKEN"),
        }));
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        userError: t("public.P__PUBLIC__ERROR_SOMETHING_WENT_WRONG"),
      }));
    }
  };

  const handleRedirect = () => {
    navigate(state.renderto);
  };

  const classes = {
    ...ResetPasswordStyleObject(theme),
    ...GuestLoginSignUpPasswordCommonCss(theme),
    ...CommonStyleObject(theme),
  };
  const primaryButtonClass = {
    ...SignUpStyleObject(theme).primaryBtn,
    marginTop: "20px",
    width: "100%",
    padding: "8px",
  };
  const cardBodyClasses = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    p: "24px",
  };
  const alertClasses = {
    width: "100%",
    letterSpacing: ".15px",
    lineHeight: "24px",
    fontSize: "16px !important",
  };
  const loginCompleted =
    (userDetails.isAuthenticated && state.loginSucess) || state.forceRedirect;

  return (
    <div>
      <Helmet>
        <title>{t("login.L__LOGIN_RESET_TITLE_TEXT")}</title>
      </Helmet>
      {!state.loading ? (
        <GridContainer sx={classes.mainLayout}>
          {loginCompleted && state.termsCondition ? (
            <Navigate to={state.renderto} replace={true} />
          ) : (
            <>
              {loginCompleted && !state.termsCondition ? (
                <>
                  <Typography variant="h5" sx={classes.setPasswordTitle}>
                    {t("common.COMMON_RESET_PASSWORD_TITLE")}
                  </Typography>
                  <Card sx={classes.cardShadow}>
                    <CardBody sx={cardBodyClasses}>
                      <Alert severity="success" sx={alertClasses}>
                        {t("common.COMMON__PASSWORD_CHANGED_SUCCESSFULLY")}
                      </Alert>
                      <Button sx={primaryButtonClass} onClick={handleRedirect}>
                        {t("common.COMMON__CONTINUE")}
                      </Button>
                    </CardBody>
                  </Card>
                </>
              ) : (
                <Formik
                  initialValues={{
                    password: "",
                    cnfpassword: "",
                    acceptTerms: false,
                    privacyPolicy: false,
                    gdprDataSharing: false,
                    isGdpr: theme.staticContent.IsGDPRContentDataSharing,
                  }}
                  onSubmit={handleSubmit}
                  validationSchema={object().shape({
                    password: string()
                      .required(t("common.COMMON__FIELD_REQUIRED_MESSAGE"))
                      .min(8, t("common.COMMON__PASSWORD_GUIDE"))
                      .matches(
                        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#_?&^+=-])[A-Za-z\d$@$!%*#_?&^+=-]{8,}$/,
                        t("common.COMMON__SET_PASSWORD_CONDITIONS")
                      ),
                    cnfpassword: string()
                      .required(t("common.COMMON__FIELD_REQUIRED_MESSAGE"))
                      .oneOf(
                        [ref("password")],
                        t("common.COMMON__EQUAL_TO_PASSWORD")
                      ),
                    acceptTerms: boolean().required(""),
                    gdprDataSharing: boolean().when("isGdpr", {
                      is: true,
                      then: boolean(),
                    }),
                  })}
                >
                  {(formikProps) => (
                    <Form style={{ width: "100%" }}>
                      <Typography variant="h5" sx={classes.setPasswordTitle}>
                        {t("common.COMMON__SET_NEW_PASSWORD")}
                      </Typography>
                      <Card sx={classes.padAll}>
                        {(state.invalidToken || state.userError) && (
                          <Box sx={{ p: "15px 15px 9px 15px" }}>
                            <Alert severity="error" sx={classes.invalidtoken}>
                              {state.invalidToken || state.userError}
                            </Alert>
                          </Box>
                        )}
                        <CardBody sx={{ pb: "0px" }}>
                          <Field
                            component={FormikTextField}
                            label={t("common.COMMON__NEW_PASSWORD")}
                            name="password"
                            onChange={handlePassword}
                            fullWidth
                            inputProps={{ maxLength: 20 }}
                            InputProps={{
                              endAdornment: !isEdgeBrowser ? (
                                <Button
                                  onClick={() =>
                                    togglePasswordVisibility("passwordShown")
                                  }
                                  sx={classes.eyeIconButton}
                                >
                                  <InputAdornment
                                    position="end"
                                    sx={classes.eyeHeight}
                                  >
                                    <FontAwesomeIcon
                                      style={classes.eyeIconCss}
                                      icon={
                                        state.passwordShown === "password"
                                          ? faEye
                                          : faEyeSlash
                                      }
                                    />
                                  </InputAdornment>
                                </Button>
                              ) : null,
                              type: state.passwordShown,
                            }}
                          />
                          <Field
                            component={FormikTextField}
                            label={t("common.COMMON__CONFIRM_NEW_PASSWORD")}
                            fullWidth
                            name="cnfpassword"
                            onChange={handlePassword}
                            inputProps={{ maxLength: 20 }}
                            InputProps={{
                              endAdornment: !isEdgeBrowser ? (
                                <Button
                                  onClick={() =>
                                    togglePasswordVisibility(
                                      "passwordShownRecheck"
                                    )
                                  }
                                  sx={classes.eyeIconButton}
                                >
                                  <InputAdornment
                                    position="end"
                                    sx={classes.eyeHeight}
                                  >
                                    <FontAwesomeIcon
                                      style={classes.eyeIconCss1}
                                      icon={
                                        state.passwordShownRecheck ===
                                        "password"
                                          ? faEye
                                          : faEyeSlash
                                      }
                                    />
                                  </InputAdornment>
                                </Button>
                              ) : null,
                              type: state.passwordShownRecheck,
                            }}
                          />
                          {state.termsCondition && (
                            <>
                              <Field
                                name="acceptTerms"
                                component={FormikCheckBox}
                                checkboxProps={{ color: "primary" }}
                                label={
                                  <Box component="div">
                                    <Typography sx={classes.fntSz_16}>
                                      {t(
                                        "common.COMMON__LOGIN_TERMS_AND_CONDITION"
                                      )}
                                    </Typography>
                                    <Button
                                      type="button"
                                      sx={classes.TermsCondition}
                                      onClick={() =>
                                        handleTermCondition("", hostURL)
                                      }
                                    >
                                      {t("common.COMMON__TERMS_AND_CONDITIONS")}
                                    </Button>
                                  </Box>
                                }
                              />
                            </>
                          )}
                          {state.termsCondition &&
                            theme.staticContent.IsGDPRContentDataSharing && (
                              <Field
                                name="gdprDataSharing"
                                component={FormikCheckBox}
                                checkboxProps={{ color: "primary" }}
                                label={
                                  <Box component="span" sx={classes.fntSz_16}>
                                    {t(
                                      "common.COMMON__GDPR_CONSENT_DESCRIPTION"
                                    )}
                                  </Box>
                                }
                              />
                            )}
                          {state.termsCondition && (
                            <GridContainer>
                              <GridItem
                                xs={12}
                                sm={12}
                                md={12}
                                lg={12}
                                sx={{ px: "0px" }}
                              >
                                <Field
                                  name="privacyPolicy"
                                  component={FormikCheckBox}
                                  checkboxProps={{ color: "primary" }}
                                  label={
                                    <div>
                                      <Box
                                        component="span"
                                        sx={classes.fntSz_16}
                                      >
                                        {t("common.COMMON__ACKNOWLEDGE_TEXT")}
                                      </Box>
                                      <br />
                                      <Button
                                        type="button"
                                        sx={classes.TermsCondition}
                                        onClick={() =>
                                          handleTermCondition("/pp", hostURL)
                                        }
                                      >
                                        {t(
                                          "common.COMMON__AMALGAM__NOVO_PRIVACY_POLICY"
                                        )}
                                      </Button>
                                      <br />
                                      <Button
                                        type="button"
                                        sx={classes.TermsCondition}
                                        onClick={() =>
                                          handleuserguide(props, hostURL)
                                        }
                                      >
                                        {t("common.COMMON__USER_GUIDE_LINK")}
                                      </Button>
                                    </div>
                                  }
                                />
                              </GridItem>
                            </GridContainer>
                          )}
                        </CardBody>
                        <CardFooter
                          sx={classes.cardFootercss}
                          disabled={formikProps.isSubmitting}
                        >
                          <GridContainer>
                            <GridItem
                              xs={12}
                              sm={12}
                              md={12}
                              lg={12}
                              sx={{ pt: "20px", px: "0px" }}
                            >
                              <Button
                                type="submit"
                                sx={[
                                  classes.primaryBtn,
                                  classes.btn,
                                  { width: "100%" },
                                ]}
                                size="small"
                                disabled={
                                  (state.termsCondition &&
                                    (!formikProps.values.acceptTerms ||
                                      !formikProps.values.privacyPolicy)) ||
                                  state.isLoginCompleted
                                }
                              >
                                {t("common.COMMON__CONFIRM_BUTTON")}
                              </Button>
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12} lg={12}>
                              <Box
                                component={Link}
                                to={"/guest/Login/"}
                                css={classes.linkcss}
                              >
                                {t("common.COMMON__LOGOUT_LINK")}
                              </Box>
                            </GridItem>
                          </GridContainer>
                        </CardFooter>
                      </Card>
                    </Form>
                  )}
                </Formik>
              )}
            </>
          )}
        </GridContainer>
      ) : (
        <CircularProgress size={36} sx={classes.buttonProgress1} />
      )}
    </div>
  );
}

ResetPassword.propTypes = {
  changeUser: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withTranslation()(
  withMediaQuery()(withRouter(WithUser(ResetPassword)))
);
